import { useCallback, useEffect, useMemo } from "react";
import { includes, isEmpty } from "lodash";
import { DefaultValues, useForm } from "react-hook-form";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { getConfigurationFieldsFromWizardTemplate } from "@radarly/models/insight-pages/wizard/configuration";
import { WizardTemplate } from "@radarly/models/insight-pages/wizard/model";
import { WizardConfiguration } from "@radarly/models/insight-pages/wizard/model/configuration";

type Configuration = Omit<WizardConfiguration, "query" | "queries"> & {
  query: number;
};

export function useWizardConfigurationForm<T extends Partial<Configuration>>(
  defaultValues: DefaultValues<T>,
  updateConfiguration: (configuration: DefaultValues<T>) => void,
  template: WizardTemplate
) {
  const locale = useCurrentUserLocale();

  const {
    handleSubmit,
    control,
    getValues,
    reset,
    formState: { isSubmitSuccessful, isValid, isDirty },
  } = useForm<T>({
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues() as DefaultValues<T>);
    }
  }, [isSubmitSuccessful, getValues, reset]);

  const applyModification = (configuration: DefaultValues<T>) => {
    updateConfiguration(configuration);
  };

  const onCancel = useCallback(
    (event?: React.BaseSyntheticEvent) => {
      reset(defaultValues);
      event.preventDefault();
      event.stopPropagation();
    },
    [reset, defaultValues]
  );
  const values = getValues();
  const hasValidMandatoryFields = useMemo(() => {
    const fields = getConfigurationFieldsFromWizardTemplate(template);
    if (isEmpty(values.title)) {
      return false;
    }
    if (includes(fields, "query") && !values.query) {
      return false;
    }
    //@ts-ignore
    if (includes(fields, "queries") && isEmpty(values.queries)) {
      return false;
    }
    return true;
  }, [values, template]);

  return {
    //@ts-ignore
    onApply: handleSubmit(applyModification),
    onCancel,
    values,
    control,
    variant: "primary" as const,
    size: "l" as const,
    buttons: {
      submit: {
        label: tr(locale, "INSIGHT_PAGE_WIZARD::CONFIGURATION_APPLY"),
        isDisabled: !isValid || !isDirty || !hasValidMandatoryFields,
      },
      cancel: {
        label: tr(locale, "INSIGHT_PAGE_WIZARD::CONFIGURATION_CANCEL"),
        isDisabled: !isDirty,
      },
    },
  };
}
